import React from "react";
import { Image as ImageType } from "../../types";
import { buildClassName, setImageParameters } from "../../utils";
import NukaCarousel from "nuka-carousel";
import Icon from "../icon";
import Image from "../image";
import Pricing, { Price } from "../pricing";
import { compact } from "lodash";
import ModalPortal from "../modal-portal";
import Rating from "../rating";
import { useI18next } from "gatsby-plugin-react-i18next";

interface RelatedProductModalProps {
  active?: boolean;
  headerImage?: ImageType;
  images?: ImageType[];
  destinationName?: string;
  productName?: string;
  rating?: number;
  description?: string;
  summary?: string;
  price?: Price;
  onCloseClick?: () => void;
}

const RelatedProductModal: React.FC<RelatedProductModalProps> = ({
  active,
  headerImage,
  images,
  destinationName,
  productName,
  rating,
  description,
  summary,
  price,
  onCloseClick,
}) => {
  const handleCloseClick: React.MouseEventHandler<HTMLButtonElement | HTMLDivElement> = () => {
    if (onCloseClick) {
      onCloseClick();
    }
  };

  const { t } = useI18next();

  const arrowStyle = {
    width: "3.5rem",
    height: "3.5rem",
    borderRadius: "50%",
    color: "#000",
    backgroundColor: "rgba(255, 255, 255, 0.5)",
  };

  return (
    <ModalPortal>
      <div
        className={buildClassName([
          "modal",
          "modal--sm",
          "modal--excursion",
          active && "modal--active",
        ])}
      >
        <div className="modal__backdrop" onClick={handleCloseClick}></div>
        <div className="modal__frame">
          <div className="modal__actions">
            <button
              type="button"
              className="button button--close"
              title={t("CLOSE")}
              onClick={handleCloseClick}
            >
              <span className="button__icon"></span>
            </button>
          </div>
          <div className="modal__body">
            <div className="excursion">
              {images && images.length > 0 ? (
                <NukaCarousel
                  slideIndex={0}
                  defaultControlsConfig={{
                    prevButtonText: "<",
                    prevButtonStyle: {
                      marginLeft: "1rem",
                      ...arrowStyle,
                    },
                    nextButtonText: ">",
                    nextButtonStyle: {
                      marginRight: "1rem",
                      ...arrowStyle,
                    },
                  }}
                >
                  {compact([headerImage, ...images]).map((image, index) => (
                    <Image
                      key={`${image.url}_${index}`}
                      type="16-9"
                      src={setImageParameters(image?.url ?? "", { mode: "max", width: 768 })}
                      alt={image?.altText ?? undefined}
                      title={t("SHOW_DETAILS")}
                    />
                  ))}
                </NukaCarousel>
              ) : (
                <Image
                  type="16-9"
                  src={setImageParameters(headerImage?.url ?? "", { mode: "max", width: 768 })}
                  alt={headerImage?.altText ?? undefined}
                  title={t("SHOW_DETAILS")}
                />
              )}
              <div className="modal__product">
                <div className="modal__product-heading">
                  {destinationName && (
                    <div className="destination-labels">
                      <div className="destination-label">
                        <Icon name="ui-marker" />
                        {destinationName}
                      </div>
                    </div>
                  )}
                  <h4>{productName}</h4>
                </div>
                {price && <Pricing {...price} />}
              </div>
              {rating && <Rating rating={rating} />}
              {description && (
                <div
                  className="modal__description"
                  dangerouslySetInnerHTML={{ __html: description }}
                ></div>
              )}
              {summary && (
                <div className="modal__summary" dangerouslySetInnerHTML={{ __html: summary }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default RelatedProductModal;
